import React, { useState, useEffect, useRef } from 'react';
import clientCandidateDataService from '../../../services/clientCandidateDataService';
import LocationSearch from '../../common/LocationSearch';

/**
 * CandidateTrackingWidget
 * Contains: Locations (multiple), Referral Source, Relationships
 */

const CandidateTrackingWidget = ({ profile, candidateId, onRefresh }) => {
  const [activeForm, setActiveForm] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationIsPrimary, setLocationIsPrimary] = useState(false);
  const [relationshipForm, setRelationshipForm] = useState({ candidate_id: '', candidate_name: '', relationship_type: '' });

  // Relocation city form
  const [isAddingCity, setIsAddingCity] = useState(false);
  const [cityForm, setCityForm] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Local mirror of clientData. Toggles + saves update this immediately so the
  // UI reflects the change without waiting for a parent re-fetch. The prop is
  // re-synced into local state whenever it changes (e.g. SSE update from
  // another tab, or parent refresh from outside this widget).
  const [clientData, setClientData] = useState(profile?.client_data || {});
  useEffect(() => {
    setClientData(profile?.client_data || {});
  }, [profile?.client_data]);

  // ── Last call ──────────────────────────────────────────────────────
  // Must sit BELOW the clientData declaration above: `lastCallDate` reads it
  // at render time (not inside a callback), so declaring it earlier hits the
  // const temporal dead zone and throws
  // "Cannot access 'clientData' before initialization" on mount.
  //
  // The backdating row is collapsed by default so the common case — punch
  // "now" — stays a single click.
  const [isEditingCallDate, setIsEditingCallDate] = useState(false);
  const lastCallDate = clientData.last_call_date || null;


  // Relative while counting days is still useful, absolute after that. The
  // exact timestamp is always available on hover.
  const formatLastCall = (iso) => {
    if (!iso) return '';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return '';
    const days = Math.floor((Date.now() - then.getTime()) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days}d ago`;
    return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const locations = clientData.locations || [];
  const relationships = clientData.relationships || [];

  const relationshipTypes = [
    'Manager', 'Direct Report', 'Colleague', 'Mentor', 'Mentee', 
    'Reference', 'Referral', 'Former Manager', 'Former Report', 'Other'
  ];

  // Local state for text fields — saves on debounce, no refresh needed
  const [noticePeriod, setNoticePeriod] = useState(clientData.notice_period ?? '');
  const [nonCompete, setNonCompete] = useState(clientData.non_compete ?? '');
  const [localReferral, setLocalReferral] = useState(clientData.referral_source || '');
  const [editingReferral, setEditingReferral] = useState(false);
  const debounceRef = useRef(null);

  // Sync local state when profile changes
  useEffect(() => {
    setNoticePeriod(clientData.notice_period ?? '');
    setNonCompete(clientData.non_compete ?? '');
    setLocalReferral(clientData.referral_source || '');
  }, [clientData.notice_period, clientData.non_compete, clientData.referral_source]);

  // Debounced save — waits 600ms after last change before saving. Updates the
  // local mirror immediately so the value is reflected without re-fetch.
  const debounceSave = (updates) => {
    setClientData(prev => ({ ...prev, ...updates }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        // Clean status is no longer reset here — marking a profile unclean
        // is reserved for the Positions widget only.
        await clientCandidateDataService.updateData(candidateId, { ...clientData, ...updates }, 'tracking');
      } catch (e) { console.error('Debounce save error:', e); }
    }, 600);
  };

  // Immediate save for toggles. Optimistic update first so the UI reflects
  // the new value instantly; on failure we revert and surface an alert.
  const saveData = async (updates) => {
    const previous = clientData;
    const optimistic = { ...previous, ...updates };
    setClientData(optimistic);
    try {
      // Clean status is no longer reset here — marking a profile unclean is
      // reserved for the Positions widget only.
      await clientCandidateDataService.updateData(candidateId, { ...optimistic }, 'tracking');
      // Intentionally no onRefresh — the local mirror is authoritative for
      // this widget. The prop will sync naturally on next external refresh.
    } catch (error) {
      console.error('Error saving:', error);
      setClientData(previous);
      alert('Failed to save');
    }
  };

  // Remote & Relocation
  const remoteStatus = clientData.remote_only ? 'remote_only' : clientData.open_to_remote ? 'open' : 'off';
  const setRemoteStatus = (val) => {
    if (val === 'off') saveData({ open_to_remote: false, remote_only: false });
    else if (val === 'open') saveData({ open_to_remote: true, remote_only: false });
    else if (val === 'remote_only') saveData({ open_to_remote: true, remote_only: true });
  };
  const relocationCities = clientData.relocation_cities || [];
  const openToRelocation = clientData.open_to_relocation || false;

  const handleAddCity = () => { setCityForm(''); setIsAddingCity(true); };
  const handleSaveCity = async () => {
    if (!cityForm.trim()) return;
    await saveData({ relocation_cities: [...relocationCities, cityForm.trim()] });
    setIsAddingCity(false); setCityForm('');
  };
  const handleDeleteCity = async (index) => {
    await saveData({ relocation_cities: relocationCities.filter((_, i) => i !== index) });
  };

  // Search candidates for relationships
  const searchCandidates = async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const response = await fetch(`/api/candidates/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results.filter(c => c.id !== parseInt(candidateId)));
      }
    } catch (err) { console.error('Search error:', err); }
    setIsSearching(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => searchCandidates(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Location handlers
  const handleAddLocation = () => {
    setSelectedLocation(null);
    setLocationIsPrimary(locations.length === 0);
    setActiveForm('location');
    setEditingIndex(null);
  };

  const handleEditLocation = (index) => {
    setSelectedLocation(locations[index]);
    setLocationIsPrimary(locations[index].is_primary || false);
    setEditingIndex(index);
    setActiveForm('location');
  };

  const handleSaveLocation = async () => {
    if (!selectedLocation || !selectedLocation.city) { 
      alert('Please select a location'); 
      return; 
    }
    
    const locationToSave = {
      city: selectedLocation.city,
      state: selectedLocation.state || '',
      country: selectedLocation.country || 'United States',
      is_primary: locationIsPrimary
    };
    
    let newLocations = [...locations];
    if (locationIsPrimary) {
      newLocations = newLocations.map(l => ({ ...l, is_primary: false }));
    }
    if (editingIndex !== null) {
      newLocations[editingIndex] = locationToSave;
    } else {
      newLocations.push(locationToSave);
    }
    await saveData({ locations: newLocations });
    setActiveForm(null);
    setEditingIndex(null);
    setSelectedLocation(null);
  };

  const handleDeleteLocation = async (index) => {
    if (!window.confirm('Delete this location?')) return;
    await saveData({ locations: locations.filter((_, i) => i !== index) });
  };

  // Relationship handlers
  const handleAddRelationship = () => {
    setRelationshipForm({ candidate_id: '', candidate_name: '', relationship_type: 'Colleague' });
    setSearchQuery('');
    setSearchResults([]);
    setActiveForm('relationship');
    setEditingIndex(null);
  };

  const handleEditRelationship = (index) => {
    setRelationshipForm({ ...relationships[index] });
    setEditingIndex(index);
    setActiveForm('relationship');
  };

  const handleSelectCandidate = (candidate) => {
    setRelationshipForm({ ...relationshipForm, candidate_id: candidate.id, candidate_name: candidate.name });
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSaveRelationship = async () => {
    if (!relationshipForm.candidate_id || !relationshipForm.relationship_type) {
      alert('Please select a candidate and relationship type'); return;
    }
    let newRelationships = [...relationships];
    if (editingIndex !== null) newRelationships[editingIndex] = relationshipForm;
    else newRelationships.push(relationshipForm);
    await saveData({ relationships: newRelationships });
    setActiveForm(null);
    setEditingIndex(null);
  };

  const handleDeleteRelationship = async (index) => {
    if (!window.confirm('Delete this relationship?')) return;
    await saveData({ relationships: relationships.filter((_, i) => i !== index) });
  };

  const handleCancel = () => { setActiveForm(null); setEditingIndex(null); };

  const formatLocation = (loc) => [loc.city, loc.state, loc.country].filter(Boolean).join(', ');

  return (
    <div className="space-y-4">
      {/* Locations */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Locations</label>
          {activeForm !== 'location' && (
            <button onClick={handleAddLocation} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add
            </button>
          )}
        </div>

        {activeForm === 'location' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
            <h4 className="font-medium text-sm text-emerald-900 mb-2">{editingIndex !== null ? 'Edit' : 'Add'} Location</h4>
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">Search Location</label>
              <LocationSearch
                value={selectedLocation}
                onChange={setSelectedLocation}
                placeholder="Search for a city..."
              />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <input 
                type="checkbox" 
                id="is_primary" 
                checked={locationIsPrimary} 
                onChange={(e) => setLocationIsPrimary(e.target.checked)} 
                className="rounded" 
              />
              <label htmlFor="is_primary" className="text-xs text-gray-700">Primary location</label>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSaveLocation} className="px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700">{editingIndex !== null ? 'Update' : 'Add'}</button>
              <button onClick={() => { setActiveForm(null); setSelectedLocation(null); }} className="px-3 py-1 border text-xs rounded hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}

        {locations.length === 0 && activeForm !== 'location' ? (
          <p className="text-[11px] text-gray-400">No locations added</p>
        ) : (
          <div className="space-y-1">
            {locations.map((loc, index) => (
              <div key={index} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded group">
                <div className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                  <span className="text-[11px] text-gray-800">{formatLocation(loc)}</span>
                  {loc.is_primary && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">Primary</span>}
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex gap-0.5">
                  <button onClick={() => handleEditLocation(index)} className="p-0.5 text-gray-400 hover:text-emerald-700"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                  <button onClick={() => handleDeleteLocation(index)} className="p-0.5 text-gray-400 hover:text-red-600"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Relocation */}
        <div className="mt-3 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-600">Open to Relocation</label>
            <button onClick={() => { saveData({ open_to_relocation: !openToRelocation, ...(!openToRelocation ? {} : { relocation_cities: [] }) }); }}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${openToRelocation ? 'bg-emerald-600' : 'bg-gray-200'}`}>
              <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${openToRelocation ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>
          {openToRelocation && (
            <div className="pl-3 border-l-2 border-emerald-200 space-y-1">
              {relocationCities.map((city, i) => (
                <div key={i} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded group text-xs">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                    <span className="text-gray-700">{city}</span>
                  </div>
                  <button onClick={() => handleDeleteCity(i)} className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))}
              {isAddingCity ? (
                <div className="flex gap-1.5">
                  <input type="text" value={cityForm} onChange={(e) => setCityForm(e.target.value)} autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveCity()}
                    className="flex-1 px-2 py-1 text-xs border rounded" placeholder="City, State" />
                  <button onClick={handleSaveCity} className="px-2 py-1 bg-emerald-600 text-white text-[10px] rounded">Add</button>
                  <button onClick={() => setIsAddingCity(false)} className="px-2 py-1 border text-[10px] rounded">✕</button>
                </div>
              ) : (
                <button onClick={handleAddCity} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Add city
                </button>
              )}
            </div>
          )}

          {/* Remote */}
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-600">Remote Work</label>
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
              {[{ id: 'off', label: 'No' }, { id: 'open', label: 'Open' }, { id: 'remote_only', label: 'Only' }].map(opt => (
                <button key={opt.id} onClick={() => setRemoteStatus(opt.id)}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded-md transition-all ${
                    remoteStatus === opt.id 
                      ? (opt.id === 'remote_only' ? 'bg-green-600 text-white' : opt.id === 'open' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 shadow-sm')
                      : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Last Call Date.
            Replaces the old "Spoken To" boolean. A yes/no flag went stale the
            moment it was set — it told you a call happened once, never when.
            This punches a timestamp instead, and the date stays on screen so
            "when did we last speak" is answerable at a glance.

            spoken_to is still written alongside for backwards compatibility:
            existing saved views, the archive quick-filters, and the candidate
            table column all read that boolean, so dropping it here would
            silently break those filters. Logging a call sets it true; clearing
            the date sets it false.

            Backdating is supported via the small calendar toggle — forgetting
            to log a call until the next day is common, and without it the only
            option would be a wrong timestamp. */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-600">Last Call</label>
            <div className="flex items-center gap-1.5">
              {lastCallDate ? (
                <span
                  className="text-[11px] font-medium text-gray-800 tabular-nums"
                  title={new Date(lastCallDate).toLocaleString()}
                >
                  {formatLastCall(lastCallDate)}
                </span>
              ) : (
                <span className="text-[11px] text-gray-400">No call logged</span>
              )}

              <button
                onClick={() => saveData({ last_call_date: new Date().toISOString(), spoken_to: true })}
                title="Log a call as of right now"
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {lastCallDate ? 'Log again' : 'Log call'}
              </button>

              <button
                onClick={() => setIsEditingCallDate(v => !v)}
                title="Backdate or clear"
                className={`p-1 rounded transition-colors ${isEditingCallDate ? 'text-emerald-700 bg-emerald-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          {isEditingCallDate && (
            <div className="mt-2 flex items-center gap-1.5">
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={lastCallDate ? new Date(lastCallDate).toISOString().slice(0, 10) : ''}
                onChange={(e) => {
                  if (!e.target.value) return;
                  saveData({ last_call_date: new Date(`${e.target.value}T12:00:00`).toISOString(), spoken_to: true });
                }}
                className="flex-1 px-2 py-1 text-xs border rounded"
              />
              {lastCallDate && (
                <button
                  onClick={() => { saveData({ last_call_date: null, spoken_to: false }); setIsEditingCallDate(false); }}
                  className="px-2 py-1 text-[10px] border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Contractual Obligations */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <label className="text-xs font-medium text-gray-600 mb-2 block">Contractual Obligations</label>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[11px] text-gray-600">Notice Period</label>
              <div className="flex items-center gap-1">
                <input type="number" min="0" value={noticePeriod}
                  onChange={(e) => { setNoticePeriod(e.target.value); debounceSave({ notice_period: e.target.value === '' ? null : parseInt(e.target.value) }); }}
                  placeholder="—"
                  className="w-14 px-2 py-1 text-[11px] text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                <span className="text-[10px] text-gray-400 w-7">days</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-[11px] text-gray-600">Non-Compete</label>
              <div className="flex items-center gap-1">
                <input type="number" min="0" value={nonCompete}
                  onChange={(e) => { setNonCompete(e.target.value); debounceSave({ non_compete: e.target.value === '' ? null : parseInt(e.target.value) }); }}
                  placeholder="—"
                  className="w-14 px-2 py-1 text-[11px] text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                <span className="text-[10px] text-gray-400 w-7">days</span>
              </div>
            </div>
            {(parseInt(noticePeriod) > 0 || parseInt(nonCompete) > 0) && (
              <div className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded">
                <span className="text-[10px] text-gray-400">Total</span>
                <span className="text-[11px] font-medium text-gray-700">{(parseInt(noticePeriod) || 0) + (parseInt(nonCompete) || 0)} days</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Referral Source */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Referral Source</label>
        {editingReferral ? (
          <div className="flex gap-1.5">
            <input type="text" value={localReferral} onChange={(e) => setLocalReferral(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { debounceSave({ referral_source: localReferral }); setEditingReferral(false); } if (e.key === 'Escape') { setLocalReferral(clientData.referral_source || ''); setEditingReferral(false); } }}
              className="flex-1 px-2 py-1 text-[11px] border rounded focus:ring-1 focus:ring-emerald-500" placeholder="How was this candidate found?" />
            <button onClick={() => { debounceSave({ referral_source: localReferral }); setEditingReferral(false); }} className="px-2 py-1 bg-emerald-600 text-white text-[10px] rounded">Save</button>
            <button onClick={() => { setLocalReferral(clientData.referral_source || ''); setEditingReferral(false); }} className="px-2 py-1 border text-[10px] rounded">✕</button>
          </div>
        ) : (
          <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 group" onClick={() => setEditingReferral(true)}>
            <span className="text-[11px] text-gray-700">{clientData.referral_source || <span className="text-gray-400">Add source...</span>}</span>
            <svg className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          </div>
        )}
      </div>

      {/* Relationships */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Relationships</label>
          {activeForm !== 'relationship' && (
            <button onClick={handleAddRelationship} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add
            </button>
          )}
        </div>

        {activeForm === 'relationship' && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
            <h4 className="font-medium text-sm text-purple-900 mb-2">{editingIndex !== null ? 'Edit' : 'Add'} Relationship</h4>
            
            {!relationshipForm.candidate_id ? (
              <div className="mb-3">
                <label className="block text-xs text-gray-600 mb-1">Search Candidate *</label>
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full px-2 py-1 text-sm border rounded" placeholder="Type to search candidates..." />
                {isSearching && <p className="text-xs text-gray-500 mt-1">Searching...</p>}
                {searchResults.length > 0 && (
                  <div className="mt-1 border rounded bg-white max-h-40 overflow-y-auto">
                    {searchResults.map(c => (
                      <button key={c.id} onClick={() => handleSelectCandidate(c)} className="w-full text-left px-2 py-1.5 hover:bg-gray-100 text-sm">
                        <span className="font-medium">{c.name}</span>
                        {c.current_title && <span className="text-gray-500 text-xs ml-2">{c.current_title}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-3">
                <label className="block text-xs text-gray-600 mb-1">Selected Candidate</label>
                <div className="flex items-center justify-between px-2 py-1.5 bg-white border rounded">
                  <span className="text-sm font-medium">{relationshipForm.candidate_name}</span>
                  <button onClick={() => setRelationshipForm({ ...relationshipForm, candidate_id: '', candidate_name: '' })} className="text-xs text-red-600 hover:underline">Change</button>
                </div>
              </div>
            )}

            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">Relationship Type *</label>
              <select value={relationshipForm.relationship_type} onChange={(e) => setRelationshipForm({ ...relationshipForm, relationship_type: e.target.value })} className="w-full px-2 py-1 text-sm border rounded">
                <option value="">Select...</option>
                {relationshipTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="flex gap-2">
              <button onClick={handleSaveRelationship} className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700">{editingIndex !== null ? 'Update' : 'Add'}</button>
              <button onClick={handleCancel} className="px-3 py-1 border text-xs rounded hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}

        {relationships.length === 0 && activeForm !== 'relationship' ? (
          <p className="text-[11px] text-gray-400">No relationships added</p>
        ) : (
          <div className="space-y-1">
            {relationships.map((rel, index) => (
              <div key={index} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded group">
                <div className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  <a href={`/client/candidate/${rel.candidate_id}`} className="text-[11px] text-emerald-700 hover:underline">{rel.candidate_name}</a>
                  <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{rel.relationship_type}</span>
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex gap-0.5">
                  <button onClick={() => handleEditRelationship(index)} className="p-0.5 text-gray-400 hover:text-purple-600"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                  <button onClick={() => handleDeleteRelationship(index)} className="p-0.5 text-gray-400 hover:text-red-600"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateTrackingWidget;
